import React, { useEffect, useState } from "react";
import { createItemAssignment, updateItemAssignment } from "../api/itemAssignedService";
import { ItemType } from "../models/ItemTypes";
import { Rack } from "../models/rack";
import { Plus, X } from "lucide-react";
import "../styles/ItemModels.css";
import "../styles/ItemAssignment.css";
import Pagination from "../view/common/pagination/Pagination";
import { deleteData, getData } from "../api/ApiServices";
import { ApiResponse } from "../models/uomtypes";
import TableComponent from "./TableComponent";
import '../styles/common/sub-menu.css';
import '../styles/common/spinner.css';
import '../styles/common/icon-style.css';
import '../styles/common/toplayout.css';
import '../styles/common/btn.css';
import '../styles/common/custom-alert.css';
import Select from "react-select";
import CustomAlert from "../view/common/alert/CustomAlert";
import { useSearchDropdowns } from "../utils/dropdownUtils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";

const ItemAssignmentForm: React.FC = () => {
    const {
        items: itemOptions,
        racks: rackOptions,
        searchItems,
        searchRacks,
        loadItems, 
        loadRacks, 
        isLoadingItems,
        isLoadingRacks,
        setItems: setItemOptions,
        setRacks: setRackOptions
    } = useSearchDropdowns();

    const [items, setItems] = useState<ItemType[]>([]);
    const [racks, setRacks] = useState<Rack[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string | undefined>("");
    const [selectedRackId, setSelectedRackId] = useState<string | undefined>("");
    const [selectedStatus, setSelectedStatus] = useState<string>("ACTIVE");
    const [itemAssignments, setItemAssignments] = useState<any[]>([]);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentAssignmentId, setCurrentAssignmentId] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    
    const [originalItemId, setOriginalItemId] = useState<string | undefined>("");
    const [originalRackId, setOriginalRackId] = useState<string | undefined>("");
    const [originalStatus, setOriginalStatus] = useState<string>("ACTIVE");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [sortBy, setSortBy] = useState<string>("itemId.partName");
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
   
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(() => {});
    const [alertCancelAction, setAlertCancelAction] = useState<() => void>(() => {});
    const [showConfirmButton, setShowConfirmButton] = useState(true);
    const [showCancelButton, setShowCancelButton] = useState(true);
    const [confirmButtonText, setConfirmButtonText] = useState("OK");
    const [cancelButtonText, setCancelButtonText] = useState("Cancel");
    const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

    const [validationErrors, setValidationErrors] = useState<{
        itemId?: string;
        rackId?: string;
        status?: string;
    }>({});

    const dispatch = useDispatch();
    const filters = useSelector((state: RootState) => state.filters);
    const permission = useSelector((state: RootState) => state.permissions);
      
    const [initializeAssignRack, isAssignRackInitialized] = useState(false);
    
    useEffect(() => {
        dispatch(resetFilters());
        isAssignRackInitialized(true);
    }, [dispatch]);
    
    useEffect(() => {
        if (isModalOpen) {
            document.body.classList.add('modal-open');
            loadItems();
            loadRacks();
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isModalOpen, loadItems, loadRacks]);

    useEffect(() => {
        if (initializeAssignRack) {
            loadData();
        }
    }, [initializeAssignRack, currentPage, searchTerm, itemsPerPage, filters, sortBy, sortOrder]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const response: any = await getData<ApiResponse<any>>("/itemassignedracks", {
                page: currentPage,
                limit: itemsPerPage,
                search: searchTerm,
                filter: filters,
                sortBy: sortBy,
                sortOrder: sortOrder
            });

            if (response.data) {
                const validAssignments = response.data.data.map((assignment: any) => ({
                    ...assignment,
                    itemId: typeof assignment.itemId === "object" ? assignment.itemId : { _id: null, partName: "N/A", hsnCode: "N/A", internalPartNo: "N/A" },
                    rackId: typeof assignment.rackId === "object" ? assignment.rackId : { _id: null, rackName: "N/A" },
                }));

                setItemAssignments(validAssignments);
                setTotalItems(response.data.pagination.total);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            console.error("Error loading data:", error);
            configureAlert(
                "Failed to load data.",
                closeAlert,
                closeAlert,
                "OK",
                "",
                true,
                false,
                3000
            );
        }
        setIsLoading(false);
    };
   
    const closeAlert = () => {
        setShowAlert(false);
    };

    const configureAlert = (
        message: string, 
        confirmAction: () => void, 
        cancelAction: () => void = () => {}, 
        confirmText: string = "OK", 
        cancelText: string = "Cancel",
        showConfirm: boolean = true,
        showCancel: boolean = true,
        autoClose: number | null = null
    ) => {
        setAlertMessage(message);
        setAlertConfirmAction(() => confirmAction);
        setAlertCancelAction(() => cancelAction);
        setConfirmButtonText(confirmText);
        setCancelButtonText(cancelText);
        setShowConfirmButton(showConfirm);
        setShowCancelButton(showCancel);
        setAutoCloseDelay(autoClose);
        setShowAlert(true);
    };
   
    const validateForm = (): boolean => {
        const errors: {itemId?: string; rackId?: string; status?: string} = {};
        
        if (!selectedItemId) {
            errors.itemId = "Item is required";
        }
        
        if (!selectedRackId) {
            errors.rackId = "Rack is required";
        }
        
        if (isEditing && !selectedStatus) {
            errors.status = "Status is required";
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const checkForDuplicates = (): boolean => {
        const existingAssignments = isEditing 
            ? itemAssignments.filter(assignment => assignment._id !== currentAssignmentId) 
            : itemAssignments;
        
        const duplicateAssignment = existingAssignments.find(
            assignment => 
                assignment.itemId?._id === selectedItemId && 
                assignment.rackId?._id === selectedRackId
        );
        
        if (duplicateAssignment) {
            configureAlert(
                "This item is already assigned to this rack!",
                closeAlert,
                closeAlert,
                "OK",
                "",
                true,
                false,
                3000 
            );
            return true;
        }
        
        return false;
    };
   
    const checkForChanges = (): boolean => {
        if (!isEditing) return true; 
        
        return (
            selectedItemId !== originalItemId ||
            selectedRackId !== originalRackId ||
            selectedStatus !== originalStatus
        );
    };
   
    const handleSortChange = (sortByField: string, sortOrderDirection: 'asc' | 'desc') => {
        setSortBy(sortByField);
        setSortOrder(sortOrderDirection);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        if (checkForDuplicates()) {
            return; 
        }
        
        if (isEditing && !checkForChanges()) {
            setIsModalOpen(false);
            configureAlert(
                "No changes were made.",
                closeAlert,
                closeAlert,
                "OK",
                "",
                true,
                false,
                3000 
            );
            return;
        }

        setIsFormSubmitting(true);
        try {
            if (isEditing) {
                const response = await updateItemAssignment(currentAssignmentId, selectedItemId || "", selectedRackId || "", selectedStatus);
                if (response.data) {
                    setIsModalOpen(false);
                    resetFormState();
                    
                    configureAlert(
                        "Assignment updated successfully!",
                        () => {
                            loadData();
                            closeAlert();
                        },
                        closeAlert,
                        "OK",
                        "",
                        true,
                        false,
                        3000 
                    );
                }
            } else {
                const response = await createItemAssignment(selectedItemId || "", selectedRackId || "");
                if (response.data) {
                    setIsModalOpen(false);
                    resetFormState();
                    
                    configureAlert(
                        "Item assigned to rack successfully!",
                        () => {
                            loadData();
                            closeAlert();
                        },
                        closeAlert,
                        "OK",
                        "",
                        true,
                        false,
                        3000
                    );
                }
            }
        } catch (error) {
            console.error("Error saving assignment:", error);
            configureAlert(
                `Failed to ${isEditing ? "update" : "create"} assignment.`,
                closeAlert,
                closeAlert,
                "OK",
                "",
                true,
                false,
                3000 
            );
        }
        setIsFormSubmitting(false);
    };

    const handleDelete = async (assignmentId: string) => {
        const assignmentToDelete = itemAssignments.find(assignment => assignment._id === assignmentId);
        
        if (assignmentToDelete) {
            configureAlert(
                `Are you sure you want to set this assignment to INACTIVE?`,
                async () => {
                    closeAlert(); 
                    
                    setIsLoading(true);
                    try {
                        const response = await deleteData("itemassignedracks", assignmentId);
                        if (response.data) {
                            await loadData();
                            
                            configureAlert(
                                "Assignment has been set to INACTIVE successfully.",
                                closeAlert,
                                closeAlert,
                                "OK",
                                "",
                                true,
                                false,
                                3000 
                            );
                        }
                    } catch (error) {
                        console.error("Error updating assignment:", error);
                        configureAlert(
                            "Failed to update assignment status.",
                            closeAlert,
                            closeAlert,
                            "OK",
                            "",
                            true,
                            false,
                            3000 
                        );
                    }
                    setIsLoading(false);
                },
                closeAlert,
                "Yes",
                "No",
                true,
                true,
                null 
            );
        }
    };

    const handleEdit = (assignment: any) => {
        setOriginalItemId(assignment?.itemId?._id);
        setOriginalRackId(assignment?.rackId?._id);
        setOriginalStatus(assignment?.status || "ACTIVE");
        
        setSelectedItemId(assignment?.itemId?._id);
        setSelectedRackId(assignment?.rackId?._id);
        setSelectedStatus(assignment?.status || "ACTIVE");
        setIsEditing(true);
        setCurrentAssignmentId(assignment?._id);
        setValidationErrors({}); 
        setIsModalOpen(true);
    };
    
    const closeModal = () => {
        if (
            isEditing ? checkForChanges() : 
            (selectedItemId || selectedRackId)
        ) {
            configureAlert(
                "Are you sure you want to close without saving?",
                () => {
                    closeAlert();
                    
                    setIsModalOpen(false);
                    resetFormState();
                },
                closeAlert,
                "Yes",
                "No",
                true,
                true,
                null 
            );
        } else {
            setIsModalOpen(false);
            resetFormState();
        }
    };

    const resetFormState = () => {
        setSelectedItemId("");
        setSelectedRackId("");
        setSelectedStatus("ACTIVE");
        setIsEditing(false);
        setCurrentAssignmentId("");
        setValidationErrors({});

        setOriginalItemId("");
        setOriginalRackId("");
        setOriginalStatus("ACTIVE");
    };

    const openAddModal = () => {
        resetFormState();
        setIsModalOpen(true);
    };

    const columns = [
        {
            key: "itemId",
            label: "Part No",
            nameKey: "internalPartNo", 
            render: (value: any) => (typeof value === "object" ? value?.internalPartNo : "N/A"),
            sortable: true
        },
        {
            key: "itemId",
            label: "Part Name",
            nameKey: "partName",
            render: (value: any) => (typeof value === "object" ? value?.partName : "N/A"),
            sortable: true
        },
        {
            key: "rackId",
            label: "Rack Name",
            nameKey: "rackName",
            render: (value: any) => (typeof value === "object" ? value?.rackName : "N/A"),
            sortable: true
        },
        {
            key: "status",
            label: "Status",
            render: (value: any) => value || "N/A",
            sortable: true
        }
    ];

    const statusOptions = [
        { value: "ACTIVE", label: "ACTIVE" },
        { value: "INACTIVE", label: "INACTIVE" }
    ];

    const selectedItem = itemOptions.find(item => item.value === selectedItemId);
    const selectedRack = rackOptions.find(rack => rack.value === selectedRackId);

    const selectStyles = {
        container: (provided: any) => ({
            ...provided,
            zIndex: 9999
        }),
        control: (provided: any) => ({
            ...provided,
            minHeight: "38px",
            fontSize: "12px",
            borderColor: provided.isFocused ? "#007bff" : "#ddd",
            boxShadow: provided.isFocused ? "0 0 0 1px #ff7a00" : "none",
            "&:hover": {
                borderColor: "#007bff"
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 9999,
            position: "absolute",
            width: "100%",
            marginTop: "4px"
        }),
        menuPortal: (provided: any) => ({
            ...provided,
            zIndex: 9999
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected ? " #007bff" : 
                             state.isFocused ? "#deebff" : "",
            color: state.isSelected ? "" : "#333",
            padding: "8px 12px",
            fontSize: "12px"
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            padding: "0px 8px",
            minHeight: "38px"
        }),
        loadingIndicator: (provided: any) => ({
            ...provided,
            color: "#007bff"
        }),
        loadingMessage: (provided: any) => ({
            ...provided,
            color: "#333",
            fontSize: "12px"
        })
    };

    const customStyles = `
        .form-group {
            position: relative;
            margin-bottom: 20px;
            width: 100%;
        }
        
        .form-error-message {
            color: #ff6b6b;
            font-size: 12px;
            margin-top: 4px;
        }
        
        .select-container {
            margin-bottom: 20px;
            position: relative;
            z-index: 2;
            font-size:14px;
        }
        
        .select-error .react-select__control {
            border-color: #ff6b6b !important;
        }
        
        /* Modal styles to match CityMaster */
        .item-models-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
        }
        
        .item-models-modal {
            background-color: #fff;
            border-radius: 8px;
            width: 80%;
            max-width: 400px;
            max-height: 80vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        }
        
        .item-models-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 5px;
            border-bottom: 1px solid #eee;
        }
        
        .item-models-modal-header h5 {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
        }
        
        .item-models-modal-close {
            background: none;
            border: none;
            cursor: pointer;
            padding: 3px;
        }
        
        .modal-form-container {
            padding: 15px;
            overflow-y: auto;
            max-height: calc(80vh - 50px);
        }
        .submit-button {
             width: 100%;
            padding: 8px;
            background-color: #4caf50;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 600;
            margin-top: 10px;
        }
        
        /* Fix for modal open state */
        body.modal-open {
            overflow: hidden;
        }
        
        .selected-part-info {
            padding: 8px;
           
         
            background-color: #f9f9f9;
            font-size: 16px;
           
        }
    `;

    return (
        <div className="item-models-container">
            <style>{customStyles}</style>
            
            <div className="item-models-header">
                <div className="border">
                    <div className="start-end-layout">
                        <div className="breadcrumb-nav">
                            <span>Master Item</span>
                            <span>Assign Item to Rack</span>
                        </div>
                        {permission.menus.itemassignedracks.create ?
                        <div className="items-button-container tooltip-wrapper">
                            <button className="item-models-add-btn" onClick={openAddModal}>
                                <Plus size={16} />
                            </button>
                        </div>
                        : <></>}
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="spinner"></div>
            ) : (
                <div className="box-container">
                    <TableComponent
                        columns={columns}
                        data={itemAssignments}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        setFilter={(filters: Record<string, any>) => {
                            Object.entries(filters).forEach(([key, value]) => {
                                dispatch(updateFilters({ key, value: String(value) }));
                            });
                        }}
                        setSorting={handleSortChange}
                        defaultSortConfig={{ key: sortBy, direction: sortOrder }}
                    />

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        totalItems={totalItems}
                    />
                </div>
            )}

            {isModalOpen && (
                <div className="item-models-modal-overlay">
                    <div className="item-models-modal">
                        <div className="item-models-modal-header">
                            <h5>{isEditing ? "Edit Assignment" : "Add New Assignment"}</h5>
                            <button className="item-models-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-form-container">
    <form onSubmit={handleSubmit} noValidate>
        <div className="select-container">
            <label htmlFor="partNoSelect">Select Part Number:</label>
            <Select
                id="partNoSelect"
                name="itemId"
                placeholder='Select Part Number'
                options={itemOptions}
                value={itemOptions.find(option => option.value === selectedItemId)}
                onChange={(selectedOption) => {
                    setSelectedItemId(selectedOption ? selectedOption.value : "");
                
                    if (validationErrors.itemId) {
                        setValidationErrors(prev => ({ ...prev, itemId: undefined }));
                    }
                }}
                onInputChange={(inputValue) => {
                    searchItems(inputValue);
                }}
                isSearchable
                noOptionsMessage={({inputValue}) => 
                    inputValue ? "No parts found" : "Type to search parts"
                }
                className={validationErrors.itemId ? "select-error" : ""}
                classNamePrefix="react-select"
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                menuPlacement="auto"
            />
            {validationErrors.itemId && (
                <div className="form-error-message">{validationErrors.itemId}</div>
            )}
        </div>
        
        {selectedItemId && selectedItem && (
            <div className="form-group">
                <label>Part Name:</label>
                <div className="selected-part-info">
                    {selectedItem?.partName || "N/A"}
                </div>
            </div>
        )}
        
        <div className="select-container">
            <label htmlFor="rackSelect">Select Rack:</label>
            <Select
                id="rackSelect"
                name="rackId"
                options={rackOptions}
                value={rackOptions.find(option => option.value === selectedRackId)}
                onChange={(selectedOption) => {
                    setSelectedRackId(selectedOption ? selectedOption.value : "");
                
                    if (validationErrors.rackId) {
                        setValidationErrors(prev => ({ ...prev, rackId: undefined }));
                    }
                }}
                onInputChange={(inputValue) => {
                    searchRacks(inputValue);
                }}
                placeholder="Search Rack"
                isSearchable
                noOptionsMessage={({inputValue}) => 
                    inputValue ? "No racks found" : "Type to search racks"
                }
                className={validationErrors.rackId ? "select-error" : ""}
                classNamePrefix="react-select"
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition={'fixed'}
                menuPlacement="auto"
            />
            {validationErrors.rackId && (
                <div className="form-error-message">{validationErrors.rackId}</div>
            )}
        </div>
        
        {isEditing && (
            <div className="select-container">
                <label htmlFor="statusSelect">Status:</label>
                <Select
                    id="statusSelect"
                    name="status"
                    options={statusOptions}
                    value={statusOptions.find(option => option.value === selectedStatus)}
                    onChange={(selectedOption) => {
                        setSelectedStatus(selectedOption ? selectedOption.value : "ACTIVE");
                       
                        if (validationErrors.status) {
                            setValidationErrors(prev => ({ ...prev, status: undefined }));
                        }
                    }}
                    placeholder="Select status"
                    className={validationErrors.status ? "select-error" : ""}
                    classNamePrefix="react-select"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition={'fixed'}
                    menuPlacement="auto"
                />
                {validationErrors.status && (
                    <div className="form-error-message">{validationErrors.status}</div>
                )}
            </div>
        )}
        
        <button type="submit" className="submit-button" disabled={isFormSubmitting}>
            {isEditing ? "Update" : "Add"}
        </button>
    </form>
</div>
                    </div>
                </div>
            )}

            {showAlert && (
                <CustomAlert 
                    message={alertMessage} 
                    onConfirm={alertConfirmAction} 
                    onCancel={alertCancelAction}
                    confirmText={confirmButtonText}
                    cancelText={cancelButtonText}
                    showConfirmButton={showConfirmButton}
                    showCancelButton={showCancelButton}
                    autoCloseDelay={autoCloseDelay}
                />
            )}
        </div>
    );
};

export default ItemAssignmentForm;